import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { BreadcrumbItem } from "./PageBreadcrumb.js";

interface BreadcrumbContextValue {
  items: BreadcrumbItem[];
  setItems: (items: BreadcrumbItem[]) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue | null>(null);

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<BreadcrumbItem[]>([]);
  const value = useMemo(() => ({ items, setItems }), [items]);

  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
}

export function useBreadcrumbItems(): BreadcrumbItem[] {
  const context = useContext(BreadcrumbContext);
  if (!context) {
    throw new Error("useBreadcrumbItems must be used within BreadcrumbProvider");
  }
  return context.items;
}

export function useSetPageBreadcrumbs(items: BreadcrumbItem[]) {
  const context = useContext(BreadcrumbContext);
  if (!context) {
    throw new Error("useSetPageBreadcrumbs must be used within BreadcrumbProvider");
  }

  const { setItems } = context;
  const serializedItems = JSON.stringify(items);

  useEffect(() => {
    setItems(items);
    return () => setItems([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- serialized trail content
  }, [setItems, serializedItems]);
}
